import { Row } from "@tanstack/react-table";
import { Folder } from "lucide-react";
import { useNavigate } from "react-router-dom";

import AvatarText from "@/components/reusables/AvatarText";
import { CustomDropdown } from "@/components/reusables/custom-action-button";
import { CustomTable } from "@/components/reusables/data-table/CustomTable";
import { Checkbox } from "@/components/ui/checkbox";
import { SELECTOR_ID } from "@/constants/global-constants";
import { ROUTES } from "@/constants/routes_sidebar/routes-constant";
import { ToolsConstants } from "@/constants/tools.constant";
import useGetFilter from "@/hooks/useGetFilter";
import { ResponsePagination } from "@/hooks/usePagination";
import { GlobalRowDataTypes } from "@/interfaces/global-table.interface";
import { ExtendedColumnDef } from "@/interfaces/globals";
import { ITransferRequest } from "@/interfaces/tools.interface";
import { cn, renderFullName } from "@/lib/utils";
import { dateFormat } from "@/utils/helper-functions/date-time.helper";

interface TransferRequestTableProps {
  data: ITransferRequest[];
  toggleTransferModal: () => void;
  pagination: ResponsePagination;
}

const TransferRequestTable = ({
  data,
  toggleTransferModal,
  pagination,
}: TransferRequestTableProps): JSX.Element => {
  const navigate = useNavigate();
  const { TRANSFER_REQUEST } = ToolsConstants.REQUEST;
  const { projectList } = useGetFilter(["project"]);

  const handleNavigate = (id: string): void => {
    navigate(`${ROUTES.TOOLS}${ROUTES.TRANSFER_REQUEST}/${id}`);
  };

  const getActionOptions = (
    row: Row<ITransferRequest>,
  ): ActionButtonDataTypes[] => [
    {
      icon: Folder,
      name: "View Products",
      onclick: (): void => {
        handleNavigate(row.original.id);
      },
    },
  ];

  const columns: ExtendedColumnDef<ITransferRequest>[] = [
    {
      map: {
        key: "id",
        valueFrom: "id",
      },
      id: SELECTOR_ID,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
          className={cn(
            "hidden group-hover/header:block",
            table.getIsSomePageRowsSelected() && "block",
            table.getIsAllPageRowsSelected() && "!block",
          )}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
          onClick={(e) => e.stopPropagation()}
        />
      ),

      size: 12,
      enableSorting: false,
      enableHiding: false,
    },

    {
      map: {
        key: "Project (From)",
        valueFrom: "projectFrom.name",
      },
      accessorKey: "Project (From)",
      header: "Project (From)",
      cell: ({ row }) => row.original.projectFrom?.name,
      meta: {
        minWidth: 200,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Project (To)",
        valueFrom: "projectTo.name",
      },
      accessorKey: "Project (To)",
      header: "Project (To)",
      cell: ({ row }) => row.original.projectTo?.name,
      meta: {
        minWidth: 200,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Transferred By",
        dataTransformer: (data) =>
          renderFullName({
            params: data.transferredBy,
          }),
      },
      accessorKey: "Transferred By",
      header: "Transferred By",
      cell: ({ row }): JSX.Element => {
        return (
          <AvatarText
            src={row?.original?.transferredBy?.image}
            name={renderFullName({
              params: row.original.transferredBy,
            })}
            title={renderFullName({
              params: row.original.transferredBy,
            })}
          />
        );
      },
      meta: {
        minWidth: 200,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Requested At",
        dataTransformer: (data) => dateFormat(data.createdAt, "dd/MM/yyyy"),
      },
      accessorKey: "requested At",
      header: "Requested At",
      cell: ({ row }) => dateFormat(row.original.createdAt, "dd/MM/yyyy"),
      meta: {
        minWidth: 150,
      },
    },
    {
      id: "actions",
      cell: ({ row }): React.JSX.Element => (
        <CustomDropdown data={getActionOptions(row)} />
      ),
      meta: {
        classNameRow: "w-14",
      },
    },
  ];

  return (
    <CustomTable
      title={TRANSFER_REQUEST.TITLE}
      columns={columns}
      data={data}
      empty={{}}
      hasWritePermission={true}
      hide={{
        filters: true,
        functions: true,
      }}
      filters={{
        dateRange: true,
        projectId: { label: "Project", options: projectList },
      }}
      functions={{
        search: TRANSFER_REQUEST.TITLE,
        isExport: true,
        columnToggle: true,
        pagination,
        add: {
          label: TRANSFER_REQUEST.ADD.label,
          handleClick: toggleTransferModal,
        },
        onRowClick: handleNavigate,
        RightClickData: (
          row: GlobalRowDataTypes<ITransferRequest>,
        ): ActionButtonDataTypes[] =>
          getActionOptions(row as Row<ITransferRequest>),
      }}
    />
  );
};

export default TransferRequestTable;
